import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import TopBar from "@/components/TopBar";
import ShareButton from "@/components/ShareButton";
import { useAuth } from "@/context/AuthContext";
import { BRANCH_META } from "@/lib/branches";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
    ArrowLeft, Rocket, Users, Globe, Check, Circle, HandHeart, Send, Hammer,
    Target, DollarSign, Zap, Plus, X, ArrowRight, Sparkles, Wand2,
} from "lucide-react";

const BOUNTY_STATUS_META = {
    listed: { label: "Open", color: "#3EB489", bg: "#DBF3E9" },
    claimed: { label: "In progress", color: "#FFC107", bg: "#FFF3CC" },
    submitted: { label: "Under review", color: "#1E88E5", bg: "#DCEBFC" },
    awarded: { label: "Awarded", color: "#6F00FF", bg: "#EADEFF" },
};

export default function ProjectDetail() {
    const { slug } = useParams();
    const { user } = useAuth();
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);
    const [interested, setInterested] = useState(false);
    const [bounties, setBounties] = useState([]);

    // Post-bounty modal
    const [showBountyModal, setShowBountyModal] = useState(false);
    const [bRewardType, setBRewardType] = useState("xp");
    const [bTitle, setBTitle] = useState("");
    const [bDesc, setBDesc] = useState("");
    const [bAmount, setBAmount] = useState(100);
    const [posting, setPosting] = useState(false);

    // AI bounty recommendations
    const [recommendations, setRecommendations] = useState(null);
    const [recommending, setRecommending] = useState(false);
    const [recError, setRecError] = useState(null);

    const load = () => {
        api.get(`/projects/${slug}`).then((r) => setData(r.data)).catch(() => setError("Project not found."));
        api.get(`/bounties/browse?project_slug=${slug}`).then((r) => setBounties(r.data || []));
    };

    useEffect(load, [slug]);

    const expressInterest = async () => {
        setBusy(true);
        try {
            const { data: r } = await api.post(`/projects/${slug}/interest`);
            setInterested(true);
            toast.success(r.already ? "You already expressed interest." : "Interest noted — the lead was pinged.");
            load();
        } catch {
            toast.error("Couldn't record your interest.");
        } finally {
            setBusy(false);
        }
    };

    const postBounty = async (e) => {
        e.preventDefault();
        setPosting(true);
        try {
            const { data: r } = await api.post("/bounties", {
                project_slug: slug,
                title: bTitle,
                description: bDesc,
                reward_type: bRewardType,
                amount: Number(bAmount),
                origin_url: window.location.origin,
            });
            if (r.checkout_url) {
                toast.info("Redirecting to Stripe to complete listing fee…");
                window.location.href = r.checkout_url;
                return;
            }
            toast.success("Bounty posted.");
            setShowBountyModal(false);
            setBTitle("");
            setBDesc("");
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't post that bounty.");
        } finally {
            setPosting(false);
        }
    };

    const fetchRecommendations = async () => {
        setRecommending(true);
        setRecError(null);
        try {
            const { data: r } = await api.post(`/bounties/recommend/${slug}`);
            setRecommendations(r.bounties || []);
        } catch (err) {
            setRecError(err?.response?.data?.detail || "Couldn't generate suggestions.");
        } finally {
            setRecommending(false);
        }
    };

    const applyRecommendation = (rec) => {
        setBRewardType(rec.reward_type);
        setBTitle(rec.title);
        setBDesc(rec.description);
        setBAmount(rec.amount);
        setShowBountyModal(true);
    };

    if (error) return <div className="min-h-screen flex flex-col items-center justify-center gap-4"><h1 className="font-display font-black text-3xl">{error}</h1><Link to="/projects" className="arcade-btn" data-variant="primary"><Rocket className="w-4 h-4" /> All projects</Link></div>;
    if (!data) return <div className="min-h-screen flex items-center justify-center text-slate-500 font-display">Loading project…</div>;

    const { project, interested_count, discipline } = data;
    const meta = discipline ? BRANCH_META[discipline.branch] : BRANCH_META.business;
    const doneCount = (project.milestones || []).filter((m) => m.done).length;
    const totalMs = (project.milestones || []).length;
    const isOwner = user && String(user.id) === String(project.created_by);
    const feeCents = bRewardType === "cash" ? Math.max(100, Math.ceil(Number(bAmount) * 100 * 0.10)) : 0;

    return (
        <div className="min-h-screen w-full">
            <TopBar />
            <div className="max-w-4xl mx-auto pt-24 pb-16 px-4 sm:px-8">
                <Link to="/projects" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900" data-testid="back-to-projects">
                    <ArrowLeft className="w-4 h-4" /> All projects
                </Link>

                <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="crystal rounded-3xl p-6 sm:p-8 mt-4 relative overflow-hidden"
                    style={{ background: `radial-gradient(circle at 90% 10%, ${meta.soft} 0%, #fff 60%)` }}
                >
                    <div className="flex items-start gap-3 flex-wrap">
                        <span className="chip" style={{ background: meta.soft, color: meta.text }}>
                            <Rocket className="w-3 h-3" /> {discipline?.name || project.discipline}
                        </span>
                        <span className="chip" style={{ background: BRANCH_META.arts.soft, color: BRANCH_META.arts.text }}>
                            <Hammer className="w-3 h-3" /> {project.org}
                        </span>
                        {project.is_user_created && (
                            <span className="text-[10px] uppercase tracking-widest font-bold text-slate-400">
                                Community-launched
                            </span>
                        )}
                        <span className="ml-auto text-xs text-slate-500 inline-flex items-center gap-1">
                            <Users className="w-3 h-3" /> {interested_count} interested
                        </span>
                    </div>
                    <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter text-slate-900 mt-3 leading-none" data-testid="project-title">
                        {project.title}
                    </h1>
                    <p className="text-slate-700 mt-4 leading-relaxed text-lg" data-testid="project-summary">
                        {project.summary}
                    </p>

                    <div className="mt-4 flex justify-end">
                        <ShareButton path={`/projects/${project.slug}`} title={project.title} refCode={user?.referral_code} />
                    </div>

                    <div className="mt-5 flex flex-wrap gap-3 text-xs text-slate-500">
                        <span>
                            Lead:{" "}
                            {project.lead_username ? (
                                <Link to={`/u/${project.lead_username}`} className="font-bold text-slate-900 hover:text-humanities" data-testid="project-lead-link">
                                    {project.lead_name}
                                </Link>
                            ) : (
                                <span className="font-bold text-slate-900">{project.lead_name}</span>
                            )}
                        </span>
                        {project.website && (
                            <a
                                href={project.website.startsWith("http") ? project.website : `https://${project.website}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 hover:text-humanities"
                                data-testid="project-website-link"
                            >
                                <Globe className="w-3 h-3" /> {project.website.replace(/^https?:\/\//, "").slice(0, 40)}
                            </a>
                        )}
                    </div>

                    {project.tags?.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-1.5">
                            {project.tags.map((t) => (
                                <span key={t} className="chip text-[10px]" style={{ background: meta.soft, color: meta.text, padding: "0.15rem 0.55rem" }}>
                                    #{t}
                                </span>
                            ))}
                        </div>
                    )}

                    <div className="mt-6 flex flex-wrap gap-2">
                        <button
                            onClick={expressInterest}
                            disabled={busy || interested}
                            className="arcade-btn"
                            data-variant={interested ? "ghost" : discipline?.branch || "primary"}
                            data-testid="express-interest"
                        >
                            {interested ? (<><Check className="w-4 h-4" /> Interest noted</>) : (<><HandHeart className="w-4 h-4" /> I'm interested</>)}
                        </button>
                        {discipline?.slug && (
                            <Link to={`/discipline/${discipline.slug}`} className="arcade-btn" data-variant="ghost" data-testid="open-discipline">
                                Open discipline hub
                            </Link>
                        )}
                        {isOwner && (
                            <button
                                onClick={() => setShowBountyModal(true)}
                                className="arcade-btn ml-auto"
                                data-variant="primary"
                                data-testid="post-bounty-cta"
                            >
                                <Target className="w-4 h-4" /> Post a bounty
                            </button>
                        )}
                    </div>
                </motion.div>

                {/* Bounties section */}
                <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} className="mt-6 crystal rounded-3xl p-6" data-testid="project-bounties">
                    <div className="flex items-center gap-2 mb-3 flex-wrap">
                        <Target className="w-5 h-5" color={BRANCH_META.arts.color} />
                        <h2 className="font-display font-bold text-xl tracking-tight text-slate-900">
                            Bounties on this project
                        </h2>
                        <span className="text-xs uppercase tracking-widest font-bold text-slate-400">
                            {bounties.length}
                        </span>
                        {isOwner && (
                            <button onClick={() => setShowBountyModal(true)} className="ml-auto arcade-btn" data-variant="ghost" style={{ padding: "0.35rem 0.75rem", fontSize: "0.75rem" }} data-testid="post-bounty-inline">
                                <Plus className="w-3 h-3" /> Post bounty
                            </button>
                        )}
                    </div>
                    {bounties.length === 0 && (
                        <p className="text-sm text-slate-500">
                            {isOwner ? "You haven't posted any bounties yet — add one to attract contributors, or let AI suggest one below." : "No bounties on this project yet."}
                        </p>
                    )}
                    <div className="space-y-2">
                        {bounties.map((b) => {
                            const st = BOUNTY_STATUS_META[b.status] || BOUNTY_STATUS_META.listed;
                            const RIcon = b.reward_type === "cash" ? DollarSign : Zap;
                            return (
                                <Link key={b.id} to={`/bounties/${b.id}`} className="rounded-2xl border border-slate-200 bg-white/80 p-4 hover:border-slate-400 transition-colors flex items-center gap-4 group" data-testid={`project-bounty-${b.id}`}>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className="chip text-[10px]" style={{ background: st.bg, color: st.color, padding: "0.15rem 0.55rem" }}>
                                                {st.label}
                                            </span>
                                            <span className="font-display font-bold text-slate-900">{b.title}</span>
                                        </div>
                                        <p className="text-xs text-slate-600 mt-0.5 line-clamp-1">{b.description}</p>
                                    </div>
                                    <div className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 font-display font-black text-sm flex-shrink-0" style={{ background: b.reward_type === "cash" ? BRANCH_META.trades.soft : BRANCH_META.humanities.soft, color: b.reward_type === "cash" ? BRANCH_META.trades.text : BRANCH_META.humanities.text }}>
                                        <RIcon className="w-3.5 h-3.5" />
                                        {b.reward_type === "cash" ? `$${b.amount_usd}` : `${b.amount_xp}`}
                                    </div>
                                    <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-slate-700 group-hover:translate-x-0.5 transition-all" />
                                </Link>
                            );
                        })}
                    </div>

                    {/* AI bounty recommender — owner only */}
                    {isOwner && (
                        <div className="mt-4 rounded-2xl border border-dashed border-slate-300 p-4" style={{ background: `linear-gradient(135deg, ${BRANCH_META.humanities.soft}44, transparent)` }} data-testid="ai-bounty-recommender">
                            {!recommendations && !recommending && !recError && (
                                <div className="flex items-start gap-3">
                                    <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: BRANCH_META.humanities.soft }}>
                                        <Wand2 className="w-5 h-5" style={{ color: BRANCH_META.humanities.color }} />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="font-display font-bold text-slate-900">
                                            Let AI design your first bounty
                                        </div>
                                        <p className="text-sm text-slate-600 mt-0.5 leading-relaxed">
                                            Claude reads your project + milestones and proposes 2 sharply-scoped bounties (one XP-friendly, one cash) you could post right now.
                                        </p>
                                    </div>
                                    <button
                                        onClick={fetchRecommendations}
                                        className="arcade-btn flex-shrink-0"
                                        data-variant="primary"
                                        data-testid="ai-suggest-bounty"
                                    >
                                        <Sparkles className="w-4 h-4" /> Suggest bounties
                                    </button>
                                </div>
                            )}
                            {recommending && (
                                <div className="flex items-center gap-2 text-sm text-slate-500">
                                    <div className="w-4 h-4 border-2 border-slate-300 border-t-slate-900 rounded-full animate-spin" />
                                    Designing bounties from your project…
                                </div>
                            )}
                            {recError && !recommending && (
                                <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-xl p-3">
                                    {recError}
                                    <button onClick={fetchRecommendations} className="arcade-btn ml-2" data-variant="ghost" style={{ padding: "0.25rem 0.6rem", fontSize: "0.7rem" }}>
                                        Retry
                                    </button>
                                </div>
                            )}
                            {recommendations && (
                                <div className="space-y-3">
                                    <div className="flex items-center gap-2">
                                        <Wand2 className="w-4 h-4" style={{ color: BRANCH_META.humanities.color }} />
                                        <div className="text-xs uppercase tracking-widest font-bold text-slate-500">
                                            AI suggestions
                                        </div>
                                        <button onClick={fetchRecommendations} className="ml-auto text-xs text-slate-500 hover:text-slate-900 font-display font-bold" data-testid="ai-regenerate">
                                            Regenerate
                                        </button>
                                    </div>
                                    {recommendations.map((rec, i) => {
                                        const RIcon = rec.reward_type === "cash" ? DollarSign : Zap;
                                        return (
                                            <div key={i} className="rounded-2xl bg-white border border-slate-200 p-4" data-testid={`ai-rec-${i}`}>
                                                <div className="flex items-start gap-3">
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center gap-2 flex-wrap">
                                                            <div className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-display font-black" style={{ background: rec.reward_type === "cash" ? BRANCH_META.trades.soft : BRANCH_META.humanities.soft, color: rec.reward_type === "cash" ? BRANCH_META.trades.text : BRANCH_META.humanities.text }}>
                                                                <RIcon className="w-3 h-3" />
                                                                {rec.reward_type === "cash" ? `$${rec.amount}` : `${rec.amount} XP`}
                                                            </div>
                                                            <span className="text-[10px] uppercase tracking-widest font-bold text-slate-400">
                                                                ~{rec.scope_hours}h scope
                                                            </span>
                                                        </div>
                                                        <div className="font-display font-bold text-slate-900 mt-1.5 leading-tight">
                                                            {rec.title}
                                                        </div>
                                                        <p className="text-sm text-slate-700 mt-1 leading-relaxed">{rec.description}</p>
                                                        {rec.reasoning && (
                                                            <p className="text-xs italic text-slate-500 mt-2">
                                                                <Sparkles className="w-3 h-3 inline mr-1" />
                                                                {rec.reasoning}
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>
                                                <button
                                                    onClick={() => applyRecommendation(rec)}
                                                    className="arcade-btn mt-3 w-full sm:w-auto"
                                                    data-variant={discipline?.branch || "primary"}
                                                    data-testid={`ai-post-${i}`}
                                                >
                                                    <Target className="w-4 h-4" /> Post this bounty
                                                </button>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    )}
                </motion.div>

                {totalMs > 0 && (
                    <motion.div
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.08 }}
                        className="mt-6 crystal rounded-3xl p-6"
                    >
                        <div className="flex items-center gap-2 mb-3">
                            <h2 className="font-display font-bold text-xl tracking-tight text-slate-900">
                                Milestones
                            </h2>
                            <span className="text-xs uppercase tracking-widest font-bold text-slate-400">
                                {doneCount}/{totalMs}
                            </span>
                        </div>
                        <ul className="space-y-2">
                            {project.milestones.map((m, i) => (
                                <li key={i} className="flex items-center gap-3 text-sm" data-testid={`milestone-${i}`}>
                                    {m.done ? <Check className="w-5 h-5" style={{ color: meta.color }} /> : <Circle className="w-5 h-5 text-slate-300" />}
                                    <span className={m.done ? "line-through text-slate-400" : "text-slate-800"}>{m.name}</span>
                                </li>
                            ))}
                        </ul>
                    </motion.div>
                )}

                <div className="mt-8 text-center">
                    <Link to="/projects/new" className="arcade-btn" data-variant="primary" data-testid="post-similar-project">
                        <Send className="w-4 h-4" /> Post a project like this
                    </Link>
                </div>
            </div>

            <AnimatePresence>
                {showBountyModal && (
                    <motion.div
                        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        className="fixed inset-0 z-40 bg-slate-900/50 backdrop-blur-md flex items-center justify-center p-4"
                        onClick={() => setShowBountyModal(false)}
                    >
                        <motion.form
                            onClick={(e) => e.stopPropagation()}
                            onSubmit={postBounty}
                            initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, opacity: 0 }}
                            className="crystal rounded-3xl p-6 w-full max-w-md relative"
                            data-testid="bounty-modal"
                        >
                            <button type="button" onClick={() => setShowBountyModal(false)} className="absolute top-4 right-4 text-slate-400 hover:text-slate-900" data-testid="bounty-modal-close">
                                <X className="w-5 h-5" />
                            </button>
                            <p className="chip" style={{ background: BRANCH_META.arts.soft, color: BRANCH_META.arts.text }}>
                                <Target className="w-3 h-3" /> New bounty on {project.title}
                            </p>
                            <h2 className="font-display font-black text-2xl tracking-tighter text-slate-900 mt-2">
                                Post a bounty
                            </h2>

                            <div className="mt-4 flex gap-1.5">
                                {[
                                    { k: "xp", label: "XP", icon: Zap },
                                    { k: "cash", label: "Cash", icon: DollarSign },
                                ].map(({ k, label, icon: Icon }) => (
                                    <button
                                        key={k}
                                        type="button"
                                        onClick={() => setBRewardType(k)}
                                        className={`flex-1 rounded-2xl border p-3 text-sm font-display font-bold flex items-center gap-2 justify-center transition-all ${bRewardType === k ? "bg-slate-900 text-cream border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"}`}
                                        data-testid={`bounty-reward-${k}`}
                                    >
                                        <Icon className="w-4 h-4" /> {label}
                                    </button>
                                ))}
                            </div>

                            <input
                                className="field-input mt-3"
                                placeholder="Bounty title"
                                value={bTitle}
                                onChange={(e) => setBTitle(e.target.value)}
                                maxLength={140}
                                required
                                data-testid="bounty-title"
                            />
                            <textarea
                                className="field-input mt-2 min-h-[100px]"
                                placeholder="What needs to be done and what does 'done' look like?"
                                value={bDesc}
                                onChange={(e) => setBDesc(e.target.value)}
                                maxLength={2000}
                                required
                                data-testid="bounty-description"
                            />
                            <div className="mt-3">
                                <label className="text-xs uppercase tracking-widest font-bold text-slate-400 mb-1.5 block">
                                    {bRewardType === "cash" ? "Amount (USD, $10 – $5,000)" : "XP stake (25 – 2,000)"}
                                </label>
                                <input
                                    type="number"
                                    className="field-input"
                                    value={bAmount}
                                    onChange={(e) => setBAmount(e.target.value)}
                                    min={bRewardType === "cash" ? 10 : 25}
                                    max={bRewardType === "cash" ? 5000 : 2000}
                                    required
                                    data-testid="bounty-amount"
                                />
                                {bRewardType === "cash" && (
                                    <div className="mt-2 text-xs text-slate-600 rounded-xl bg-blue-50 border border-blue-200 p-3 leading-relaxed" data-testid="bounty-fee-note">
                                        <strong>Listing fee:</strong> ${(feeCents / 100).toFixed(2)} — you'll be sent to Stripe to pay this now.
                                        Payout of the ${bAmount} bounty to the contributor happens directly between you two, off Project Atlas.
                                    </div>
                                )}
                                {bRewardType === "xp" && (
                                    <div className="mt-2 text-xs text-slate-600">
                                        You stake this XP from your balance now. It transfers to the contributor on award (or is refunded on cancel).
                                    </div>
                                )}
                            </div>

                            <div className="mt-4 flex justify-end gap-2">
                                <button type="button" onClick={() => setShowBountyModal(false)} className="arcade-btn" data-variant="ghost">Cancel</button>
                                <button className="arcade-btn" data-variant="primary" disabled={posting} data-testid="bounty-post-submit">
                                    <Target className="w-4 h-4" /> {posting ? "Posting…" : bRewardType === "cash" ? "Continue to payment" : "Post bounty"}
                                </button>
                            </div>
                        </motion.form>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}
